const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

// en-CA formats as YYYY-MM-DD. Deliberately not toISOString(), which returns
// UTC and rolls over to tomorrow's date during Mountain Time evenings.
export const todayLocal = () => new Date().toLocaleDateString("en-CA")

const parts = (date) => date.split("-").map(Number)

export function formatSearchDate(date) {
  const [year, month, day] = parts(date)
  return `${MONTHS[month - 1]} ${day} ${year}`
}

export function buildFlightSearchUrl(flight) {
  const carrier = (flight.airlineCode || flight.airline || "").trim()
  const tokens = [carrier, flight.flightNumber, "flight"]
  if (flight.origin && flight.destination) {
    tokens.push(flight.origin, "to", flight.destination)
  }
  tokens.push(formatSearchDate(flight.date))
  const q = tokens.filter(Boolean).join(" ")
  return `https://www.google.com/search?q=${encodeURIComponent(q)}`
}

export function partitionFlights(flights, today = todayLocal()) {
  const upcoming = flights
    .filter((f) => f.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
  const past = flights
    .filter((f) => f.date < today)
    .sort((a, b) => b.date.localeCompare(a.date))
  return { upcoming, past }
}

export function daysUntil(date, today = todayLocal()) {
  const [ay, am, ad] = parts(date)
  const [by, bm, bd] = parts(today)
  // Both sides built in UTC so DST transitions cannot skew the difference.
  const ms = Date.UTC(ay, am - 1, ad) - Date.UTC(by, bm - 1, bd)
  return Math.round(ms / 86400000)
}

export function relativeDayLabel(date, today = todayLocal()) {
  const days = daysUntil(date, today)
  if (days === 0) return "Today"
  if (days === 1) return "Tomorrow"
  return null
}

export function formatCardDate(date) {
  const [year, month, day] = parts(date)
  // Constructed as a local date so the rendered day always matches the string.
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

// Users routinely paste the whole designator ("DL1234", "F9 123"). The carrier
// prefix has to come off so the stored number matches a hand-typed one. A
// letters-only strip is not enough: F9, G4 and B6 all carry a digit, so "F9123"
// would degrade to "9123". Strip the flight's own code first when it leaves a
// pure number behind, and fall back to the letters-only rule otherwise (no code
// entered yet, or a code that does not match what was typed).
function normalizeFlightNumber(value, airlineCode) {
  const compact = (value || "").replace(/\s+/g, "")
  if (airlineCode && compact.toUpperCase().startsWith(airlineCode)) {
    const rest = compact.slice(airlineCode.length)
    if (/^\d+$/.test(rest)) return rest
  }
  return compact.replace(/^[A-Za-z]+/, "")
}

export function normalizeFlight(input) {
  const airlineCode = (input.airlineCode || "").trim().toUpperCase()
  return {
    who: (input.who || "").trim(),
    airline: (input.airline || "").trim(),
    airlineCode,
    flightNumber: normalizeFlightNumber(input.flightNumber, airlineCode),
    date: (input.date || "").trim(),
    origin: (input.origin || "").trim().toUpperCase(),
    destination: (input.destination || "").trim().toUpperCase(),
    note: (input.note || "").trim(),
  }
}

export function isFlightValid(input) {
  const f = normalizeFlight(input)
  return Boolean(
    f.who && (f.airline || f.airlineCode) && f.flightNumber && f.date
  )
}
